<template>
  <v-container fluid class="pa-6">

    <!-- Header -->
    <div class="d-flex align-center justify-space-between mb-6 flex-wrap gap-3">
      <div>
        <h1 class="text-h5 font-weight-bold">User Management</h1>
        <p class="text-body-2 text-medium-emphasis mt-1">
          Manage system users and their access levels
        </p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-account-plus" @click="openCreate">
        Add User
      </v-btn>
    </div>

    <!-- Stats bar -->
    <v-row class="mb-4">
      <v-col v-for="stat in stats" :key="stat.label" cols="6" sm="3">
        <v-card rounded="lg" variant="tonal" :color="stat.color" class="pa-4 text-center">
          <v-icon :icon="stat.icon" size="28" class="mb-1" />
          <div class="text-h6 font-weight-bold">{{ stat.value }}</div>
          <div class="text-caption">{{ stat.label }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card rounded="lg" class="mb-4 pa-4" elevation="0" border>
      <v-row dense align="center">
        <v-col cols="12" sm="5" md="4">
          <v-text-field
            v-model="search"
            placeholder="Search name, email, username…"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            @update:model-value="debouncedLoad"
          />
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="filterActive"
            :items="[{ title: 'All', value: '' }, { title: 'Active', value: 'true' }, { title: 'Inactive', value: 'false' }]"
            label="Status"
            variant="outlined"
            density="compact"
            hide-details
            @update:model-value="loadUsers"
          />
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="filterAdmin"
            :items="[{ title: 'All Roles', value: '' }, { title: 'Admins', value: 'true' }, { title: 'Non-admins', value: 'false' }]"
            label="Role"
            variant="outlined"
            density="compact"
            hide-details
            @update:model-value="loadUsers"
          />
        </v-col>
        <v-col cols="12" sm="1" md="4" class="d-flex justify-end">
          <v-btn variant="text" size="small" @click="resetFilters" prepend-icon="mdi-filter-remove">
            Reset
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-card rounded="lg" elevation="0" border>
      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
        hover
      >
        <!-- Name + avatar -->
        <template #[`item.full_name`]="{ item }">
          <div class="d-flex align-center gap-3 py-1">
            <v-avatar size="36" :color="avatarColor(item.full_name)" class="text-white font-weight-bold text-body-2">
              {{ initials(item.full_name) }}
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.full_name }}</div>
              <div class="text-caption text-medium-emphasis">@{{ item.username }}</div>
            </div>
          </div>
        </template>

        <!-- Role badges -->
        <template #[`item.role_name`]="{ item }">
          <div class="d-flex flex-wrap gap-1">
            <v-chip v-if="item.is_admin" color="error" size="x-small" prepend-icon="mdi-shield-account" label>
              Admin
            </v-chip>
            <v-chip v-if="item.is_board_member" color="primary" size="x-small" prepend-icon="mdi-account-group" label>
              Board Member
            </v-chip>
            <v-chip v-if="!item.is_admin && !item.is_board_member" color="default" size="x-small" prepend-icon="mdi-account" label>
              User
            </v-chip>
          </div>
        </template>

        <!-- Active status -->
        <template #[`item.is_active`]="{ item }">
          <v-chip
            :color="item.is_active ? 'success' : 'error'"
            size="x-small"
            :prepend-icon="item.is_active ? 'mdi-check-circle' : 'mdi-close-circle'"
            label
          >
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>

        <!-- Last login -->
        <template #[`item.last_login`]="{ item }">
          <span class="text-caption text-medium-emphasis">
            {{ item.last_login ? formatDate(item.last_login) : 'Never' }}
          </span>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{ item }">
          <div class="d-flex gap-1">
            <v-tooltip text="Edit user" location="top">
              <template #activator="{ props }">
                <v-btn v-bind="props" icon size="x-small" variant="text" color="primary" @click="openEdit(item)">
                  <v-icon size="16">mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Reset password" location="top">
              <template #activator="{ props }">
                <v-btn v-bind="props" icon size="x-small" variant="text" color="warning" @click="openResetPassword(item)">
                  <v-icon size="16">mdi-lock-reset</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip :text="item.is_active ? 'Deactivate' : 'Activate'" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon size="x-small" variant="text"
                  :color="item.is_active ? 'orange' : 'success'"
                  :disabled="item.id === currentUserId"
                  @click="toggleActive(item)"
                >
                  <v-icon size="16">{{ item.is_active ? 'mdi-account-off' : 'mdi-account-check' }}</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Delete user" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon size="x-small" variant="text" color="error"
                  :disabled="item.id === currentUserId || item.is_admin"
                  @click="openDelete(item)"
                >
                  <v-icon size="16">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>

        <!-- Empty -->
        <template #no-data>
          <div class="text-center py-12 text-medium-emphasis">
            <v-icon size="56" color="grey-lighten-1">mdi-account-search</v-icon>
            <p class="mt-2 text-body-2">No users found</p>
            <v-btn variant="text" color="primary" size="small" @click="resetFilters">Clear filters</v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <v-divider />
      <div class="d-flex align-center justify-space-between px-4 py-3 flex-wrap gap-2">
        <span class="text-caption text-medium-emphasis">
          Showing {{ users.length }} of {{ pagination.total || 0 }} users
        </span>
        <v-pagination
          v-model="page"
          :length="pagination.pages || 1"
          :total-visible="5"
          density="compact"
          @update:model-value="loadUsers"
        />
        <v-select
          v-model="perPage"
          :items="[10, 20, 50]"
          label="Per page"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 90px"
          @update:model-value="loadUsers"
        />
      </div>
    </v-card>

    <!-- ── Create / Edit Dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="editDialog" max-width="580" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h6 pa-6 pb-2">
          <v-icon :color="editingUser?.id ? 'primary' : 'success'" class="mr-2">
            {{ editingUser?.id ? 'mdi-account-edit' : 'mdi-account-plus' }}
          </v-icon>
          {{ editingUser?.id ? 'Edit User' : 'Create New User' }}
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-6">
          <v-form ref="editForm" v-model="editFormValid">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editData.first_name"
                  label="First Name"
                  variant="outlined"
                  density="comfortable"
                  :rules="[r => !!r || 'Required']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editData.last_name"
                  label="Last Name"
                  variant="outlined"
                  density="comfortable"
                  :rules="[r => !!r || 'Required']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editData.email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  :rules="[r => !!r || 'Required', r => /.+@.+\..+/.test(r) || 'Invalid email']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editData.username"
                  label="Username"
                  variant="outlined"
                  density="comfortable"
                  :rules="[r => !!r || 'Required']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editData.phone"
                  label="Phone (optional)"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editData.role_id"
                  :items="roles"
                  item-title="name"
                  item-value="id"
                  label="Role (optional)"
                  variant="outlined"
                  density="comfortable"
                  clearable
                />
              </v-col>
              <!-- Password field only on create -->
              <v-col v-if="!editingUser?.id" cols="12">
                <v-text-field
                  v-model="editData.password"
                  label="Initial Password"
                  :type="showEditPassword ? 'text' : 'password'"
                  :append-inner-icon="showEditPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append-inner="showEditPassword = !showEditPassword"
                  variant="outlined"
                  density="comfortable"
                  hint="Leave blank to use the system default (TempPassword123!)"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" sm="6" class="d-flex align-center">
                <v-switch
                  v-model="editData.is_admin"
                  label="System Administrator"
                  color="error"
                  hide-details
                  density="compact"
                />
              </v-col>
              <v-col cols="12" sm="6" class="d-flex align-center">
                <v-switch
                  v-model="editData.is_active"
                  label="Account Active"
                  color="success"
                  hide-details
                  density="compact"
                />
              </v-col>
              <v-col v-if="editingUser?.id" cols="12" class="d-flex align-center">
                <v-switch
                  v-model="editData.must_change_password"
                  label="Require password change on next login"
                  color="warning"
                  hide-details
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="editDialog = false">Cancel</v-btn>
          <v-btn
            :color="editingUser?.id ? 'primary' : 'success'"
            variant="elevated"
            :loading="saving"
            :disabled="!editFormValid"
            @click="saveUser"
          >
            {{ editingUser?.id ? 'Save Changes' : 'Create User' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Reset Password Dialog ─────────────────────────────────────────── -->
    <v-dialog v-model="resetDialog" max-width="440">
      <v-card rounded="lg">
        <v-card-title class="text-h6 pa-6 pb-2">
          <v-icon color="warning" class="mr-2">mdi-lock-reset</v-icon>
          Reset Password
        </v-card-title>
        <v-card-text class="px-6 pb-2">
          <p class="text-body-2 text-medium-emphasis mb-4">
            Set a new password for <strong>{{ resetTarget?.full_name }}</strong>.
            They will be required to change it on next login.
          </p>
          <v-text-field
            v-model="resetPassword"
            label="New Password"
            :type="showResetPassword ? 'text' : 'password'"
            :append-inner-icon="showResetPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showResetPassword = !showResetPassword"
            variant="outlined"
            density="comfortable"
            :rules="[r => !!r || 'Required', r => r.length >= 8 || 'Min 8 characters']"
          />
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="resetDialog = false">Cancel</v-btn>
          <v-btn color="warning" variant="elevated" :loading="saving" @click="doResetPassword">
            Reset Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Delete Confirmation ───────────────────────────────────────────── -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card rounded="lg">
        <v-card-title class="text-h6 pa-6 pb-2">
          <v-icon color="error" class="mr-2">mdi-account-remove</v-icon>
          Delete User
        </v-card-title>
        <v-card-text class="px-6 pb-4 text-body-2 text-medium-emphasis">
          Are you sure you want to permanently delete
          <strong>{{ deleteTarget?.full_name }}</strong>?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="elevated" :loading="saving" @click="doDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" :timeout="3500">
      <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useAuthStore } from '@/store/auth'
import api from '@/services/api'

export default {
  name: 'UserManagement',
  setup() {
    const authStore = useAuthStore()
    const currentUserId = computed(() => authStore.user?.id)

    // ── Table state ──────────────────────────────────────────────────────
    const users      = ref([])
    const roles      = ref([])
    const loading    = ref(false)
    const page       = ref(1)
    const perPage    = ref(20)
    const pagination = ref({})
    const search     = ref('')
    const filterActive = ref('')
    const filterAdmin  = ref('')

    const headers = [
      { title: 'Name',       key: 'full_name',  sortable: true,  minWidth: '200px' },
      { title: 'Email',      key: 'email',      sortable: true  },
      { title: 'Role',       key: 'role_name',  sortable: false },
      { title: 'Status',     key: 'is_active',  sortable: true  },
      { title: 'Last Login', key: 'last_login', sortable: true  },
      { title: 'Actions',    key: 'actions',    sortable: false, align: 'end' }
    ]

    const stats = computed(() => {
      const all    = pagination.value.total || users.value.length
      const active = users.value.filter(u => u.is_active).length
      const admins = users.value.filter(u => u.is_admin).length
      const board  = users.value.filter(u => u.is_board_member).length
      return [
        { label: 'Total Users',    value: all,    icon: 'mdi-account-group', color: 'primary'  },
        { label: 'Active',         value: active, icon: 'mdi-check-circle',  color: 'success'  },
        { label: 'Administrators', value: admins, icon: 'mdi-shield-account', color: 'error'   },
        { label: 'Board Members',  value: board,  icon: 'mdi-account-tie',   color: 'secondary' }
      ]
    })

    // ── Load data ────────────────────────────────────────────────────────
    let debounceTimer = null
    const debouncedLoad = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => { page.value = 1; loadUsers() }, 350)
    }

    const loadUsers = async () => {
      loading.value = true
      try {
        const params = { page: page.value, per_page: perPage.value }
        if (search.value)       params.search   = search.value
        if (filterActive.value) params.is_active = filterActive.value
        if (filterAdmin.value)  params.is_admin  = filterAdmin.value

        const { data } = await api.get('/users', { params })
        users.value      = data.users
        pagination.value = data.pagination
      } catch (e) {
        notify('Failed to load users', 'error', 'mdi-alert-circle')
      } finally {
        loading.value = false
      }
    }

    const loadRoles = async () => {
      try {
        const { data } = await api.get('/users/roles')
        roles.value = data.roles || []
      } catch { /* silently fail */ }
    }

    const resetFilters = () => {
      search.value = ''
      filterActive.value = ''
      filterAdmin.value = ''
      page.value = 1
      loadUsers()
    }

    // ── Create / Edit ────────────────────────────────────────────────────
    const editDialog   = ref(false)
    const editForm     = ref(null)
    const editFormValid = ref(false)
    const editingUser  = ref(null)
    const saving       = ref(false)
    const showEditPassword = ref(false)

    const editData = reactive({
      first_name: '', last_name: '', email: '', username: '',
      phone: '', role_id: null, password: '',
      is_admin: false, is_active: true, must_change_password: false
    })

    const openCreate = () => {
      editingUser.value = null
      Object.assign(editData, {
        first_name: '', last_name: '', email: '', username: '',
        phone: '', role_id: null, password: '',
        is_admin: false, is_active: true, must_change_password: false
      })
      editDialog.value = true
    }

    const openEdit = (user) => {
      editingUser.value = user
      Object.assign(editData, {
        first_name: user.first_name,
        last_name:  user.last_name,
        email:      user.email,
        username:   user.username,
        phone:      user.phone || '',
        role_id:    user.role_id || null,
        password:   '',
        is_admin:   user.is_admin,
        is_active:  user.is_active,
        must_change_password: user.must_change_password
      })
      editDialog.value = true
    }

    const saveUser = async () => {
      if (!editFormValid.value) return
      saving.value = true
      try {
        const payload = { ...editData }
        if (!payload.password) delete payload.password

        if (editingUser.value?.id) {
          await api.put(`/users/${editingUser.value.id}`, payload)
          notify('User updated successfully', 'success', 'mdi-check-circle')
        } else {
          await api.post('/users', payload)
          notify('User created successfully', 'success', 'mdi-account-check')
        }
        editDialog.value = false
        await loadUsers()
      } catch (e) {
        notify(e.response?.data?.message || 'Failed to save user', 'error', 'mdi-alert-circle')
      } finally {
        saving.value = false
      }
    }

    // ── Toggle active ────────────────────────────────────────────────────
    const toggleActive = async (user) => {
      try {
        const endpoint = user.is_active ? `/users/${user.id}/deactivate` : `/users/${user.id}/activate`
        await api.put(endpoint)
        user.is_active = !user.is_active
        notify(
          `${user.full_name} ${user.is_active ? 'activated' : 'deactivated'}`,
          user.is_active ? 'success' : 'warning',
          user.is_active ? 'mdi-account-check' : 'mdi-account-off'
        )
      } catch (e) {
        notify('Failed to update user status', 'error', 'mdi-alert-circle')
      }
    }

    // ── Reset password ───────────────────────────────────────────────────
    const resetDialog      = ref(false)
    const resetTarget      = ref(null)
    const resetPassword    = ref('')
    const showResetPassword = ref(false)

    const openResetPassword = (user) => {
      resetTarget.value   = user
      resetPassword.value = ''
      resetDialog.value   = true
    }

    const doResetPassword = async () => {
      if (!resetPassword.value || resetPassword.value.length < 8) return
      saving.value = true
      try {
        await api.put(`/users/${resetTarget.value.id}/reset-password`, {
          new_password: resetPassword.value,
          require_password_change: true
        })
        notify(`Password reset for ${resetTarget.value.full_name}`, 'success', 'mdi-lock-reset')
        resetDialog.value = false
      } catch (e) {
        notify(e.response?.data?.message || 'Failed to reset password', 'error', 'mdi-alert-circle')
      } finally {
        saving.value = false
      }
    }

    // ── Delete ───────────────────────────────────────────────────────────
    const deleteDialog = ref(false)
    const deleteTarget = ref(null)

    const openDelete = (user) => {
      deleteTarget.value = user
      deleteDialog.value = true
    }

    const doDelete = async () => {
      saving.value = true
      try {
        await api.delete(`/users/${deleteTarget.value.id}`)
        notify(`${deleteTarget.value.full_name} deleted`, 'success', 'mdi-account-remove')
        deleteDialog.value = false
        await loadUsers()
      } catch (e) {
        notify(e.response?.data?.message || 'Failed to delete user', 'error', 'mdi-alert-circle')
      } finally {
        saving.value = false
      }
    }

    // ── Helpers ──────────────────────────────────────────────────────────
    const snackbar = reactive({ show: false, text: '', color: 'success', icon: 'mdi-check-circle' })
    const notify = (text, color = 'success', icon = 'mdi-check-circle') => {
      Object.assign(snackbar, { show: true, text, color, icon })
    }

    const COLORS = ['primary', 'secondary', 'success', 'warning', 'error', 'info', 'teal', 'indigo', 'deep-purple']
    const avatarColor = (name) => COLORS[(name?.charCodeAt(0) || 0) % COLORS.length]
    const initials = (name) => name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'
    const formatDate = (iso) => {
      if (!iso) return 'Never'
      return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
    }

    onMounted(() => { loadUsers(); loadRoles() })

    return {
      users, roles, loading, page, perPage, pagination,
      search, filterActive, filterAdmin, headers, stats,
      loadUsers, debouncedLoad, resetFilters,
      editDialog, editForm, editFormValid, editingUser, editData, saving, showEditPassword,
      openCreate, openEdit, saveUser,
      toggleActive,
      resetDialog, resetTarget, resetPassword, showResetPassword, openResetPassword, doResetPassword,
      deleteDialog, deleteTarget, openDelete, doDelete,
      snackbar, avatarColor, initials, formatDate, currentUserId
    }
  }
}
</script>

<style scoped>
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
</style>
